import { useSubmitMutation } from "@/src/services/api/auth.api";
import { zodResolver } from "@hookform/resolvers/zod";
import { router } from "expo-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { z } from "zod";

const forgotPasswordSchema = z.object({
  email: z.string().min(1, "ERROR_NO_EMAIL").email("ERROR_EMAIL_NOT_VALID"),
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const useForgotPassword = () => {
  const { t } = useTranslation();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError: setFormError,
  } = form;

  const submitMutation = useSubmitMutation({
    onSuccess: async (data) => {
      const responseMessage =
        typeof data?.message === "string"
          ? data.message
          : data?.message?.error_description ||
            data?.message?.error ||
            t("SUCCESS_REQUEST_SUBMITTED");

      Toast.show({
        type: "success",
        text1: responseMessage,
      });
      router.replace("/login");
    },
    onError: (err: any) => {
      const status = err.response?.status;
      const responseMessage = err.response?.data;
      const errorMessage =
        typeof responseMessage === "string"
          ? responseMessage
          : responseMessage?.error_description || responseMessage?.errors.Email.replace('_',' ');

      if (status === 400 || status === 422) {
        setFormError("email", {
          message: errorMessage || t("ERROR_INVALID_REQUEST"),
        });
      } else if (status === 403) {
        setFormError("email", {
          message: errorMessage || t("ERROR_FORBIDDEN"),
        });
      } else if (status === 404) {
        setFormError("email", {
          message: errorMessage || t("ERROR_NOT_FOUND"),
        });
      } else if (status === 500) {
        setFormError("email", {
          message: t("ERROR_SERVER"),
        });
      } else {
        setFormError("email", {
          message: errorMessage || t("ERROR_SUBMIT_FAILED"),
        });
      }
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    submitMutation.mutate({ email: data.email });
  };

  const navigateToRegistration = () => {
    router.replace("/registration");
  };

  return {
    control,
    handleSubmit,
    errors,

    isSubmitting: submitMutation.isPending,

    onSubmit,
    navigateToRegistration,

    t,
  };
};
